import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { history } from "../../_helpers";
import authService from "../../_services/cart/authService";
import suscripcionService, { featuresDe, formatoFecha, formatoPrecio } from "../../_services/cart/suscripcion.service";

/**
 * Planes y suscripcion. Portada de tienda-admin (`components/princing-plans`, 13b5a5b): el header, el
 * banner de prueba, la grilla con el plan mas caro destacado y "Seleccionar plan".
 *
 * Diferencia con tienda-admin: aca la prueba gratis no se "inicia" con un boton, arranca sola al darse de
 * alta por el link de vendedores. El banner, en vez de ofrecerla, dice cuantos dias quedan y que no se
 * cobra nada hasta que termine.
 *
 * Es publica: sin sesion, "Seleccionar plan" lleva al registro de vendedores.
 */
export const Planes = () => {
    const [planes, setPlanes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [estado, setEstado] = useState(null);

    const logueado = authService.isAuthenticated();
    const esVendedor = authService.hasAuthority("VENDEDOR");

    useEffect(() => {
        suscripcionService
            .planes()
            .then((r) => setPlanes(r.planes || []))
            .catch(() => setPlanes([]))
            .finally(() => setLoading(false));
        if (logueado && esVendedor) {
            suscripcionService.estado(true).then(setEstado).catch(() => setEstado(null));
        }
    }, [logueado, esVendedor]);

    /** Todos los planes publicados, del mas barato al mas caro. */
    const planesActivos = [...planes].sort((a, b) => a.amount - b.amount);

    /** Destaca el plan mas caro: es el que mas incluye. */
    const esDestacado = (plan) =>
        planesActivos.length > 1 && planesActivos[planesActivos.length - 1].id === plan.id;

    const esElActual = (plan) => estado?.vigente && !estado?.trial && estado?.planId === plan.id;

    const seleccionarPlan = (plan) => {
        if (!logueado) {
            sessionStorage.setItem("selectedPlanId", plan.id);
            history.push("/registro-vendedor");
        } else {
            history.push(`/vendedor/suscripcion/checkout/${plan.id}`);
        }
    };

    return (
        <section className="planes">
            <div className="planes__header">
                <span className="planes__eyebrow">Planes &amp; Suscripción</span>
                <h2>Vendé tus autopartes online, sin vueltas</h2>
            </div>

            {/* Banner de prueba */}
            {estado?.vigente && estado?.trial && (
                <div className="planes__banner">
                    <div>
                        <h3>Estás usando deautopartes gratis</h3>
                        <p>
                            Te quedan <strong>{estado.diasRestantes} {estado.diasRestantes === 1 ? "día" : "días"}</strong> de
                            prueba (hasta el {formatoFecha(estado.endDate)}). Elegí tu plan cuando quieras: no se te cobra
                            nada hasta que termine la prueba.
                        </p>
                    </div>
                    <FontAwesomeIcon icon="fa-solid fa-gift" size="2x" />
                </div>
            )}

            {estado && !estado.vigente && estado.tieneSuscripcion && (
                <div className="planes__banner planes__banner--vencido">
                    <div>
                        <h3>{estado.trial ? "Terminó tu prueba gratis" : "Tu suscripción no está activa"}</h3>
                        <p>Tus publicaciones no se muestran hasta que elijas un plan. Apenas se acredite el pago vuelven solas.</p>
                    </div>
                    <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation" size="2x" />
                </div>
            )}

            {estado?.fundador && (
                <div className="planes__banner">
                    <div>
                        <h3>Tenés el plan Fundador</h3>
                        <p>Por estar desde el principio en deautopartes, tu cuenta no paga suscripción ni tiene límites.</p>
                    </div>
                    <FontAwesomeIcon icon="fa-solid fa-award" size="2x" />
                </div>
            )}

            {loading && <p className="planes__estado">Cargando planes...</p>}

            {!loading && planesActivos.length === 0 && (
                <p className="planes__estado">No hay planes disponibles en este momento. Escribinos y te ayudamos.</p>
            )}

            {!loading && planesActivos.length > 0 && (
                <div className="planes__grilla">
                    {planesActivos.map((plan) => (
                        <div key={plan.id} className={esDestacado(plan) ? "planes__card planes__card--destacado" : "planes__card"}>
                            <div className="planes__badges">
                                {esDestacado(plan) && <span className="planes__badge planes__badge--destacado">Más elegido</span>}
                                {plan.trialDias > 0 && <span className="planes__badge">{plan.trialDias} días gratis</span>}
                            </div>

                            <h3>{plan.name}</h3>
                            <p className="planes__descripcion">{plan.descripcion}</p>

                            <div className="planes__precio">
                                <span className="planes__precio-monto">{formatoPrecio(plan.amount)}</span>
                                <span className="planes__precio-periodo">/mes</span>
                            </div>

                            <ul className="planes__features">
                                {featuresDe(plan).map((f) => (
                                    <li key={f}>
                                        <FontAwesomeIcon icon="fa-solid fa-circle-check" />
                                        {f}
                                    </li>
                                ))}
                            </ul>

                            <button
                                type="button"
                                className="button planes__boton"
                                data-type={esDestacado(plan) ? "accent" : "secondary"}
                                disabled={esElActual(plan) || estado?.fundador}
                                onClick={() => seleccionarPlan(plan)}
                            >
                                {esElActual(plan) ? "Tu plan actual" : "Seleccionar plan"}
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
};
